import React, { useState, useRef, useEffect } from "react";
import { surveyResultIframe } from "services/_survey";

import { successToast, errorToast } from "components/toast";
import "react-quill/dist/quill.snow.css";

import Spinner from "layouts/spinner";
import SurveyReferences from "./SurveyComponent/surveyReferences";
import { AccessDenied } from "./SurveyComponent/accessDenied";
import {
  SurveyBody,
  PRIOR_DIAGNOSIS_ID,
  PRIOR_DIAGNOSIS_QUESTION,
} from "./SurveyComponent/surveyBody";
import EmailDialog from "./SurveyComponent/emailDialog";
import IframeHeader from "./SurveyComponent/IframeHeader";
import IframeFooter from "./SurveyComponent/IframeFooter";
import { getSettingsData } from "services/_settings";

const SurveyIframe = () => {
  const [display, setDisplay] = useState(false);
  const [symptomData, setSymptomData] = useState([]);
  const [diagnosticData, setDiagnosticData] = useState([]);
  const [answers, setAnswers] = useState({});
  const [graphData, setGraphData] = useState({});

  const [showChart1, setShowChart1] = useState(false);
  const [showChart2, setShowChart2] = useState(false);

  const resultClickCountRef = useRef(0);
  const [disableResults, setDisableResults] = useState(false);
  const [disableIncludeTest, setDisableIncludeTest] = useState(false);
  const [phase, setPhase] = useState(1);
  const [open, setOpen] = useState(false);
  const [resultId, setResultId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [settings, setSettings] = useState({});
  const [unanswered, setUnanswered] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);
  const [userInfo, setUserInfo] = useState({
    age: "",
    sex: "",
    ethnicity: "",
    race: "",
  });
  const [settingsLoading, setSettingsLoading] = useState(true);

  const partner = new URLSearchParams(window.location.search).get("partner");

  const isIframe = window.self !== window.top;

  const referrer = isIframe ? document.referrer : window.location.href;

  const chart1Ref = useRef(null);
  const chart2Ref = useRef(null);

  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);

      const res = await getSettingsData();
      if (res.isSuccess) {
        const settingsObj = {};
        res.data.forEach((item) => {
          settingsObj[item.key] = item.value;
        });
        setSettings(settingsObj);
      }
    } catch (err) {
      console.error("Failed to load settings", err);
    } finally {
      setSettingsLoading(false);
    }
  };
  useEffect(() => {
    if (window.self !== window.top) {
      window.parent.postMessage({ iframeMode: true }, "*");
    }

    fetchSettings();
  }, []);
  if (settingsLoading) {
    return <Spinner isFullScreen={true} />;
  }
  const isIframeEnabled = settings?.iframe_enabled === "true";

  if (isIframe) {
    if (!partner || partner !== "SAA" || !isIframeEnabled) {
      return <AccessDenied />;
    }
  }

  const beginAction = () => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(true);
    return true;
  };

  const endAction = () => {
    busyRef.current = false;
    setBusy(false);
  };

  const handleSubmit = async () => {
    let hasQuestionError = false;
    let hasError = false;

    if (disableResults || busyRef.current) return;

    // Validate all questions answered
    const requiredIds = symptomData.map((q) => q.id);
    const missing = requiredIds.filter((id) => answers[id] === undefined);
    if (answers[PRIOR_DIAGNOSIS_ID] === undefined) {
      missing.unshift(PRIOR_DIAGNOSIS_ID);
    }

    if (missing.length > 0) {
      setUnanswered(missing);
      hasQuestionError = true;
      hasError = true;
    } else {
      setUnanswered([]);
    }

    let message = "";
    if (hasError) {
      if (hasQuestionError) {
        message = "Please answer all questions before viewing results.";
      }

      errorToast(message);
      return;
    }

    if (!beginAction()) return;

    try {
    const symptomDataToSend = symptomData.map((q) => ({
      questionId: q.id,
      question: q.symptoms,
      answer: answers[q.id] ?? false,
    }));

    const diagnosticDataToSend = diagnosticData.map((q) => ({
      questionId: q.id,
      question: q.symptoms,
      answer: answers[q.id] ?? false,
    }));

    const response = await surveyResultIframe(
      {
        symptoms: symptomDataToSend,
        diagnostics: diagnosticDataToSend,
        userInfo: [
          {
            question: PRIOR_DIAGNOSIS_QUESTION,
            answer: answers[PRIOR_DIAGNOSIS_ID],
            type: "prior_diagnosis",
          },
          { question: "age", answer: userInfo.age || null, type: "other" },
          { question: "sex", answer: userInfo.sex || null, type: "other" },
          {
            question: "ethnicity",
            answer: userInfo.ethnicity || null,
            type: "other",
          },
          { question: "race", answer: userInfo.race || null, type: "other" },
        ],
      },
      {
        "X-Partner": partner,
        "X-Embed-Mode": isIframe ? "iframe" : "direct",
        "X-Referrer": referrer || "localhost",
      },
    );

    if (!response.isSuccess && response.status === 403) {
      errorToast(response.message || "Access Denied");
      setAccessDenied(true);
      return;
    }

    if (!response.isSuccess || !response.data) {
      errorToast(response.message || "Something went wrong");
      return;
    }

    const data = response;

    setGraphData(data.data);
    setResultId(data.data.id);
    successToast("Your survey result is ready");

    if (display) {
      setShowChart1(true);
      setShowChart2(true);
    } else {
      setShowChart1(true);
      setShowChart2(false);
    }

    resultClickCountRef.current += 1;

    if (phase === 1 && resultClickCountRef.current >= 2) {
      setDisableResults(true);
    }

    if (phase === 2 && resultClickCountRef.current >= 2) {
      setDisableResults(true);
      setDisableIncludeTest(true);
    }
    } catch (err) {
      errorToast("Something went wrong");
    } finally {
      endAction();
    }
  };

  if (accessDenied) {
    return <AccessDenied />;
  }
  const isMobileDevice = () => window.innerWidth <= 768;

  return (
    <>
      <div style={{ backgroundColor: "#fff" }}>
        <div className="body_overly"></div>
        <section id="surevy_container">
          {(loading || busy) && <Spinner isFullScreen={true} />}
          <IframeHeader settings={settings} />
          <SurveyBody
            symptomData={symptomData}
            diagnosticData={diagnosticData}
            answers={answers}
            setAnswers={setAnswers}
            handleSubmit={handleSubmit}
            disableResults={disableResults}
            showChart1={showChart1}
            showChart2={showChart2}
            chart1Ref={chart1Ref}
            chart2Ref={chart2Ref}
            graphData={graphData}
            display={display}
            disableIncludeTest={disableIncludeTest}
            settings={settings}
            unanswered={unanswered}
            setUnanswered={setUnanswered}
            setOpen={setOpen}
            isIframe={isIframe}
            userInfo={userInfo}
            setUserInfo={setUserInfo}
            resultId={resultId}
            setDisplay={setDisplay}
            setDisableResults={setDisableResults}
            setDisableIncludeTest={setDisableIncludeTest}
            phase={phase}
            setPhase={setPhase}
            setSymptomData={setSymptomData}
            setDiagnosticData={setDiagnosticData}
            setLoading={setLoading}
            resultClickCountRef={resultClickCountRef}
            isMobileDevice={isMobileDevice}
            busy={busy}
            beginAction={beginAction}
            endAction={endAction}
          />
          <div className="container mb-[15px]">
            <SurveyReferences />
          </div>
        </section>
        <EmailDialog
          open={open}
          settings={settings}
          setOpen={setOpen}
          beginAction={beginAction}
          endAction={endAction}
          chart1Ref={chart1Ref}
          chart2Ref={chart2Ref}
          resultId={resultId}
          isMobileDevice={isMobileDevice}
        />
        <IframeFooter />
      </div>
    </>
  );
};
export default SurveyIframe;
